import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, User as UserIcon, LogOut, Landmark } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinancingModal } from "@/components/FinancingModal";

const NAV_ITEMS = [
  { label: "България", slug: "bulgaria" },
  { label: "Чужбина", slug: "chuzhbina" },
  { label: "Банки", slug: "banki" },
  { label: "Наеми", slug: "naemi" },
];

export default function Header() {
  const [categories, setCategories] = useState([]);
  const [financingOpen, setFinancingOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-[#F8F7F3]/95 backdrop-blur-md border-b border-[#E4E7E9]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[68px]">
        <div className="flex items-center gap-7">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-2 text-[#10243A]" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#F8F7F3] w-72 overflow-y-auto">
              <SheetTitle className="sr-only">Навигация</SheetTitle>
              <SheetDescription className="sr-only">Меню за навигация на Metri.bg</SheetDescription>
              <nav className="mt-10 flex flex-col gap-1" data-testid="mobile-nav">
                <Link to="/podcasts" onClick={() => setMobileOpen(false)} className="py-2.5 text-[#C47A35] font-bold border-b border-[#E4E7E9]" data-testid="mobile-nav-podcasts">
                  Подкаст
                </Link>
                {categories.map((c) => (
                  <Link key={c.id} to={`/category/${c.slug}`} onClick={() => setMobileOpen(false)} className="py-2.5 text-[#20252B] font-medium border-b border-[#E4E7E9]" data-testid={`mobile-nav-${c.slug}`}>
                    {c.name}
                  </Link>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <button onClick={() => { setMobileOpen(false); setFinancingOpen(true); }} className="text-left py-2 text-sm font-semibold border border-[#C47A35] text-[#A6631F] px-3" data-testid="mobile-financing-button">
                    Финансиране
                  </button>
                  {!user && (
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="py-2 text-sm font-bold bg-[#10243A] text-white px-3 text-center uppercase tracking-wide" data-testid="mobile-login-link">
                      Вход
                    </Link>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="font-serif text-2xl font-bold text-[#10243A] tracking-tight" data-testid="header-logo">
            Metri<span className="text-[#C47A35]">.bg</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="relative py-2 text-[13px] font-semibold text-[#20252B] hover:text-[#10243A] transition-colors uppercase tracking-[0.06em] after:absolute after:left-0 after:-bottom-[1px] after:h-[2px] after:w-0 after:bg-[#C47A35] after:transition-all hover:after:w-full"
                data-testid={`nav-category-item-${item.slug}`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/podcasts" className="text-[13px] font-extrabold text-[#C47A35] hover:text-[#A6631F] transition-colors uppercase tracking-[0.06em]" data-testid="nav-podcasts-link">
              Подкаст
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate("/search")} className="p-2 text-[#20252B] hover:text-[#C47A35] transition-colors" data-testid="header-search-button">
            <Search className="w-[18px] h-[18px]" />
          </button>

          <button
            onClick={() => setFinancingOpen(true)}
            className="hidden md:inline-flex items-center gap-1.5 border border-[#C47A35] text-[#A6631F] hover:bg-[#F3E8DB] text-xs font-bold uppercase tracking-wide px-3.5 py-2 transition-colors"
            data-testid="header-financing-button"
          >
            <Landmark className="w-3.5 h-3.5" /> Финансиране
          </button>

          {user === null ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 text-[#20252B]" data-testid="header-account-menu">
                  <UserIcon className="w-[18px] h-[18px]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <div className="px-3 py-1.5 text-sm text-[#66717B]">{user.name}</div>
                {(user.role === "admin" || user.role === "editor") && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="header-admin-link">
                    Админ панел
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="header-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Изход
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="text-xs font-bold uppercase tracking-wide bg-[#10243A] hover:bg-[#C47A35] text-white px-3.5 py-2 transition-colors"
                data-testid="header-login-link"
              >
                Вход
              </Link>
            </div>
          )}
        </div>
      </div>
      <FinancingModal open={financingOpen} onOpenChange={setFinancingOpen} />
    </header>
  );
}
